import { db, type ContactRow } from '../db';
import type { PersonRecord } from '../schemas';
import { newId } from '@/utils/ids';
import { applyPersonnelScopeDefaults, normalizePersonnelInput } from '../personnel/personnelScope';
import {
  createSyncMetadata,
  updateSyncMetadata,
  softDeleteSyncMetadata,
} from '../sync/syncMetadata';
import { queueForSync } from '../sync/syncOutbox';
import { buildContactSyncPayload } from '../personnel/contactSync';
import { trackEntityCreated, trackEntityUpdated, trackEntityDeleted } from '@/lib/metrics';

function isActiveContact(row: ContactRow): boolean {
  // Use syncDeletedAt as the single source of truth for deletion status
  return !row.syncDeletedAt;
}

function rowToPerson(row: ContactRow): PersonRecord {
  return {
    id: row.id,
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    phone: row.phone,
    role: row.role,
    scope: row.scope ?? 'company',
    siteId: row.siteId,
    reportRecipient: row.reportRecipient ?? false,
    alarmContact: row.alarmContact ?? false,
    fireTraining: row.fireTraining ?? false,
    firstAidTraining: row.firstAidTraining ?? false,
    licensed: row.licensed ?? false,
    licenseExpiry: row.licenseExpiry,
    licenseLastUpdate: row.licenseLastUpdate,
    reperibile: row.reperibile ?? false,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    deletedAt: row.deletedAt,
  };
}

class ContactsRepoImpl {
  async listByCompany(companyId: string): Promise<PersonRecord[]> {
    const rows = await db.contacts.where('companyId').equals(companyId).toArray();
    return applyPersonnelScopeDefaults(rows.filter(isActiveContact).map(rowToPerson));
  }

  async listByCompanies(companyIds: string[]): Promise<Map<string, PersonRecord[]>> {
    if (companyIds.length === 0) {
      return new Map();
    }
    const rows = await db.contacts.where('companyId').anyOf(companyIds).toArray();
    const grouped = new Map<string, PersonRecord[]>();
    rows.filter(isActiveContact).forEach((row) => {
      const list = grouped.get(row.companyId);
      if (list) {
        list.push(rowToPerson(row));
      } else {
        grouped.set(row.companyId, [rowToPerson(row)]);
      }
    });
    return grouped;
  }

  async listBySite(companyId: string, siteId: string): Promise<PersonRecord[]> {
    const rows = await db.contacts
      .where('companyId')
      .equals(companyId)
      .filter((row) => row.siteId === siteId)
      .toArray();
    return applyPersonnelScopeDefaults(rows.filter(isActiveContact).map(rowToPerson));
  }

  async add(
    companyId: string,
    input: Omit<PersonRecord, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const now = new Date().toISOString();
    const normalized = normalizePersonnelInput(input);
    const id = newId();
    const syncMeta = createSyncMetadata();
    const row: ContactRow = {
      id,
      companyId,
      siteId: normalized.scope === 'site' ? normalized.siteId : undefined,
      firstName: normalized.firstName,
      lastName: normalized.lastName,
      email: normalized.email,
      phone: normalized.phone,
      role: normalized.role,
      scope: normalized.scope,
      reportRecipient: normalized.reportRecipient,
      alarmContact: normalized.alarmContact,
      fireTraining: normalized.fireTraining,
      firstAidTraining: normalized.firstAidTraining,
      licensed: normalized.licensed,
      licenseExpiry: normalized.licenseExpiry,
      licenseLastUpdate: normalized.licenseLastUpdate,
      reperibile: normalized.reperibile,
      createdAt: now,
      updatedAt: now,
      deletedAt: undefined,
      ...syncMeta,
    };

    await db.contacts.add(row);
    await queueForSync(
      'contacts',
      id,
      'upsert',
      buildContactSyncPayload(companyId, rowToPerson(row), null)
    );
    trackEntityCreated('contact');
    return id;
  }

  async update(
    companyId: string,
    contactId: string,
    updates: Partial<Omit<PersonRecord, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<void> {
    const existing = await db.contacts.get(contactId);
    if (!existing) throw new Error(`Personnel with id ${contactId} not found`);
    // Use syncDeletedAt as the single source of truth
    if (existing.syncDeletedAt) {
      throw new Error('Personale archiviato');
    }

    const now = new Date().toISOString();
    const previousUpdatedAt = existing.updatedAt ?? null;
    const merged = {
      ...rowToPerson(existing),
      ...updates,
      createdAt: existing.createdAt,
      updatedAt: now,
      deletedAt: existing.deletedAt,
    };
    const normalized = normalizePersonnelInput(merged);
    const syncMeta = updateSyncMetadata(existing);

    const nextRow: ContactRow = {
      ...existing,
      firstName: normalized.firstName,
      lastName: normalized.lastName,
      email: normalized.email,
      phone: normalized.phone,
      role: normalized.role,
      scope: normalized.scope,
      siteId: normalized.scope === 'site' ? normalized.siteId : undefined,
      reportRecipient: normalized.reportRecipient,
      alarmContact: normalized.alarmContact,
      fireTraining: normalized.fireTraining,
      firstAidTraining: normalized.firstAidTraining,
      licensed: normalized.licensed,
      licenseExpiry: normalized.licenseExpiry,
      licenseLastUpdate: normalized.licenseLastUpdate,
      reperibile: normalized.reperibile,
      updatedAt: now,
      ...syncMeta,
    };

    await db.contacts.put(nextRow);
    await queueForSync(
      'contacts',
      contactId,
      'upsert',
      buildContactSyncPayload(companyId, rowToPerson(nextRow), previousUpdatedAt)
    );
    trackEntityUpdated('contact');
  }

  async remove(companyId: string, contactId: string): Promise<void> {
    const existing = await db.contacts.get(contactId);
    if (!existing) return;

    const now = new Date().toISOString();
    const syncMeta = softDeleteSyncMetadata(existing);
    const nextRow: ContactRow = {
      ...existing,
      deletedAt: now,
      updatedAt: now,
      ...syncMeta,
    };

    await db.contacts.put(nextRow);
    await queueForSync(
      'contacts',
      contactId,
      'delete',
      buildContactSyncPayload(companyId, rowToPerson(nextRow), existing.updatedAt ?? null)
    );
    trackEntityDeleted('contact');
  }

  async get(contactId: string): Promise<PersonRecord | null> {
    const row = await db.contacts.get(contactId);
    if (!row || !isActiveContact(row)) return null;
    return rowToPerson(row);
  }

  async applyContactSyncUpdate(contactId: string, updates: Partial<PersonRecord>): Promise<void> {
    const existing = await db.contacts.get(contactId);
    if (!existing) return;

    const nextRow: ContactRow = {
      ...existing,
      firstName: updates.firstName ?? existing.firstName,
      lastName: updates.lastName ?? existing.lastName,
      email: updates.email ?? existing.email,
      phone: updates.phone ?? existing.phone,
      role: updates.role ?? existing.role,
      scope: updates.scope ?? existing.scope,
      siteId: updates.siteId ?? existing.siteId,
      reportRecipient: updates.reportRecipient ?? existing.reportRecipient,
      alarmContact: updates.alarmContact ?? existing.alarmContact,
      fireTraining: updates.fireTraining ?? existing.fireTraining,
      firstAidTraining: updates.firstAidTraining ?? existing.firstAidTraining,
      licensed: updates.licensed ?? existing.licensed,
      licenseExpiry: updates.licenseExpiry ?? existing.licenseExpiry,
      licenseLastUpdate: updates.licenseLastUpdate ?? existing.licenseLastUpdate,
      reperibile: updates.reperibile ?? existing.reperibile,
      updatedAt: updates.updatedAt ?? existing.updatedAt,
    };

    await db.contacts.put(nextRow);
  }
}

export const ContactsRepo = new ContactsRepoImpl();
